import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { countryName } from "./geo"
import { sdk } from "./sdk"

export type SegmentFilters = {
  marketing?: "subscribed" | "not_subscribed"
  tags_any?: string[]
  orders_min?: number
  orders_max?: number
  spent_min?: number
  spent_max?: number
  has_account?: boolean
  country?: string
  province?: string
  ordered_within_days?: number
  not_ordered_for_days?: number
  created_within_days?: number
}
export type Segment = { id: string; name: string; filters: SegmentFilters; created_at: string; count?: number }

export const useSegments = () => useQuery({ queryKey: ["segments"], queryFn: () => sdk.client.fetch<{ segments: Segment[] }>("/admin/terify/segments") })

export function useSaveSegment(id?: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { name: string; filters: SegmentFilters }) => sdk.client.fetch<{ segment: Segment }>(id ? `/admin/terify/segments/${id}` : "/admin/terify/segments", { method: "POST", body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["segments"] }),
  })
}
export function useDeleteSegment() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: (id: string) => sdk.client.fetch(`/admin/terify/segments/${id}`, { method: "DELETE" }), onSuccess: () => qc.invalidateQueries({ queryKey: ["segments"] }) })
}
export function usePreviewSegment(filters: SegmentFilters) {
  return useQuery({
    queryKey: ["segments", "preview", filters],
    queryFn: () => sdk.client.fetch<{ count: number; sample: { id: string; email: string; first_name: string | null; last_name: string | null }[] }>("/admin/terify/segments/preview", { method: "POST", body: { filters } }),
  })
}

/** Plain-language summary of a segment's rules. */
export function describeFilters(f: SegmentFilters): string[] {
  const out: string[] = []
  if (f.marketing) out.push(f.marketing === "subscribed" ? "Subscribed to email marketing" : "Not subscribed to email marketing")
  if (f.tags_any?.length) out.push(`Tagged ${f.tags_any.join(" or ")}`)
  if (f.orders_min != null) out.push(`At least ${f.orders_min} order${f.orders_min === 1 ? "" : "s"}`)
  if (f.orders_max != null) out.push(`At most ${f.orders_max} order${f.orders_max === 1 ? "" : "s"}`)
  if (f.spent_min != null) out.push(`Spent at least $${f.spent_min}`)
  if (f.spent_max != null) out.push(`Spent at most $${f.spent_max}`)
  if (f.has_account != null) out.push(f.has_account ? "Has an account" : "No account (guest)")
  if (f.country) out.push(countryName(f.country))
  if (f.province) out.push(`State/province ${f.province}`)
  if (f.ordered_within_days != null) out.push(`Ordered in the last ${f.ordered_within_days} days`)
  if (f.not_ordered_for_days != null) out.push(`No order in ${f.not_ordered_for_days}+ days`)
  if (f.created_within_days != null) out.push(`Joined in the last ${f.created_within_days} days`)
  return out.length ? out : ["Everyone"]
}
